import { Link } from 'react-router-dom';
import { LandingShell } from './LandingShell';
import {
  CreatorPartnerIcon,
  TwoPhotographersIcon,
  ModelAgentIcon,
  InfluencerManagerIcon,
} from './UseCaseIcons';

const REQUEST_ACCESS_MAILTO =
  'mailto:henrik@hallengrens.com?subject=Vuelette%20%E2%80%93%20Request%20access%20(duo)';

export default function LandingDuos() {
  return (
    <LandingShell currentPage="duos">
      <header className="vl-ap-hero">
        <div
          className="vl-hero-image-bg"
          style={{ backgroundImage: 'url(/landing-img/hero-duos-v6.jpg)' }}
          aria-hidden
        />
        <div className="vl-ap-hero-inner">
          <div className="vl-ap-hero-text">
            <Link to="/" className="vl-back-link">← For everyone</Link>
            <div className="vl-section-eyebrow">For creative duos · 03</div>
            <h1>
              Two people <em>building one body of work.</em>
            </h1>
            <p className="vl-lead">
              Vuelette was built first for a couple in Barcelona — one creator,
              one photographer — who needed a single home for the work they
              make together. If you're a content creator with a partner who
              shoots you, an influencer with a collaborator behind the camera,
              or two image-makers sharing a practice, Vuelette is the place
              where you both keep your own view of the same shoot.
            </p>
          </div>
        </div>
      </header>

      <section className="vl-section">
        <div className="vl-container">
          <div className="vl-split vl-split-right">
            <div className="vl-split-text">
              <div className="vl-section-eyebrow">Why duos struggle elsewhere</div>
              <h2 className="vl-section-title">
                Most tools assume <em>one user per account.</em>
              </h2>
              <p className="vl-section-lead">
                Shared logins don't work. Forwarded WeTransfer links don't
                work. Two people rating the same image and overwriting
                each other definitely doesn't work. Vuelette was designed
                from the start for two perspectives on one shoot.
              </p>
              <ul className="vl-features">
                <li>
                  <span className="vl-feat-icon">⌘</span>
                  <div>
                    <strong>Two ratings per frame</strong>
                    <span>Both of you give 1–5 stars to the same image. The grid shows both at once — agree, disagree, decide together.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⌷</span>
                  <div>
                    <strong>Conversations on the frame, not on Slack</strong>
                    <span>Comment on a single image. Tag specific moments to revisit. The discussion lives where the work lives.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⊞</span>
                  <div>
                    <strong>Both of you own the work</strong>
                    <span>Neither side can delete the other's view. Even if the relationship changes, both copies of the shoot remain.</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="vl-split-image">
              <img src="/landing-img/duos-parallel-v2.jpg" alt="" />
            </div>
          </div>
        </div>
      </section>

      <section className="vl-section vl-section-tinted">
        <div className="vl-container">
          <div className="vl-section-eyebrow">Use cases</div>
          <h2 className="vl-section-title">
            What creative duos use Vuelette <em>for.</em>
          </h2>

          <div className="vl-usecase-grid">
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><CreatorPartnerIcon /></div>
              <span className="vl-badge">Creator + partner</span>
              <h4>A shared workflow without shared logins.</h4>
              <p>
                The partner who shoots imports the session. The creator
                who's in it picks her favorites. Both keep their own
                metadata. One curates the brand pitch, the other curates
                the personal archive.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><TwoPhotographersIcon /></div>
              <span className="vl-badge">Two photographers</span>
              <h4>Shared sessions, parallel selection.</h4>
              <p>
                Working a wedding or editorial with a second shooter?
                Both of you pull from the same session into your own Sets.
                Each builds their own portfolio without stepping on the
                other's work.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><ModelAgentIcon /></div>
              <span className="vl-badge">Model + agent</span>
              <h4>A model's portfolio, with an agent's eye.</h4>
              <p>
                Agents help models curate. Vuelette lets your agent
                comment on individual frames, propose Sets, and approve
                portfolios — without ever needing to touch your raw work.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><InfluencerManagerIcon /></div>
              <span className="vl-badge">Influencer + manager</span>
              <h4>Brand-pitch portfolios, faster.</h4>
              <p>
                Your manager builds a pitch portfolio from your latest
                content shoots. You approve. They share. You see opens
                and engagement. The conversation never leaves the work.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="vl-cta-block">
        <div className="vl-container">
          <h2 className="vl-section-title" style={{ marginInline: 'auto' }}>
            Built for two — open for everyone.
          </h2>
          <p>
            We made Vuelette because we needed it ourselves. It's now
            open to other duos, models, and photographers who want a
            quiet, considered home for their work.
          </p>
          <a href={REQUEST_ACCESS_MAILTO} className="vl-btn vl-btn-primary">
            Request access →
          </a>
          <p style={{ marginTop: '1.5rem' }}>
            <Link to="/" style={{ color: 'var(--vl-text-3)' }}>← Back to hub</Link>
          </p>
        </div>
      </section>
    </LandingShell>
  );
}
